import { CSSProperties, FC, Fragment, KeyboardEvent, MouseEvent, ReactNode, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { Tags } from 'lucide-react';
import { TbChevronRight, TbHistory, TbSearch } from 'react-icons/tb';

import { useResourceHistory } from '@/contexts/02-Authenticated';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    BaseSkeleton,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    MergeIcon,
    TagAssignmentDropdown,
} from '@/shared';
import MergeResourceModal from '@/shared/components/modals/ResourceActionModals/04-MergeResourceModal';
import { isMergeableResourceType, ResourceHistoryTarget, SubjectReference, WorkspaceSubjectType } from '@/types';

import {
    filterWorkspaceResourceActionItems,
    getWorkspaceResourceActionItemDisabled,
    shouldRenderWorkspaceResourceActionDivider,
    WorkspaceResourceActionAccess,
    WorkspaceResourceActionMenuItem,
} from './helpers';

interface WorkspaceResourceActionsDropdownProps {
    access?: WorkspaceResourceActionAccess;
    beforeItems?: ReactNode;
    children: ReactNode;
    footer?: ReactNode;
    loading?: boolean;
    menuStyle?: CSSProperties;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
    placement?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'top' | 'topLeft' | 'topRight';
    resourceLabel: string;
    subject?: SubjectReference;
    stopPropagation?: boolean;
    triggerKey?: string;
    items: WorkspaceResourceActionMenuItem[];
    history?: ResourceHistoryTarget;
}

const defaultMenuStyle: CSSProperties = {
    maxHeight: 'calc(100vh - 4rem)',
    minWidth: 280,
};

const subjectTypeByHistoryType: Partial<Record<ResourceHistoryTarget['resourceType'], WorkspaceSubjectType>> = {
    dashboards: WorkspaceSubjectType.DASHBOARD,
    folders: WorkspaceSubjectType.FOLDER,
    importedEntities: WorkspaceSubjectType.IMPORTED_ENTITY,
    investments: WorkspaceSubjectType.INVESTMENT,
    lists: WorkspaceSubjectType.LIST,
    memos: WorkspaceSubjectType.MEMO,
    pipelines: WorkspaceSubjectType.PIPELINE,
    reports: WorkspaceSubjectType.REPORT,
    tasks: WorkspaceSubjectType.TASK,
    teams: WorkspaceSubjectType.TEAM,
};

const WorkspaceResourceActionsDropdown: FC<WorkspaceResourceActionsDropdownProps> = ({
    access,
    beforeItems,
    children,
    footer,
    history,
    items,
    loading,
    menuStyle,
    onOpenChange,
    open,
    placement = 'bottomRight',
    resourceLabel,
    subject,
    stopPropagation,
    triggerKey = 'more',
}): JSX.Element => {
    const [search, setSearch] = useState('');
    const [mergeOpen, setMergeOpen] = useState(false);
    const [tagsOpen, setTagsOpen] = useState(false);
    const { token } = useAntDesignTheme();
    const { openResourceHistory } = useResourceHistory();

    const historyItem: WorkspaceResourceActionMenuItem | undefined = history
        ? {
              icon: <TbHistory size='1rem' />,
              key: 'history',
              label: 'Show History',
              permission: 'read',
              onClick: () => openResourceHistory(history),
          }
        : undefined;
    const mergeItem: WorkspaceResourceActionMenuItem | undefined =
        history && isMergeableResourceType(history.resourceType)
            ? {
                  icon: <MergeIcon />,
                  key: 'merge',
                  label: 'Merge',
                  permission: 'update',
                  onClick: () => setMergeOpen(true),
              }
            : undefined;
    const historySubjectType = history ? subjectTypeByHistoryType[history.resourceType] : undefined;
    const tagSubject =
        subject ??
        (history && historySubjectType
            ? {
                  subjectId: history.resourceId,
                  subjectLabel: history.title,
                  subjectType: historySubjectType,
              }
            : undefined);
    const tagsItem: WorkspaceResourceActionMenuItem | undefined = tagSubject
        ? {
              icon: <Tags size='1rem' />,
              key: 'tags',
              label: 'Manage Tags',
              permission: 'update',
              onClick: () => setTagsOpen(true),
          }
        : undefined;
    const deleteIndex = items.findIndex((item) => item.key === 'delete');
    const supplementalItems = [tagsItem, mergeItem, historyItem].filter(
        (item): item is WorkspaceResourceActionMenuItem => Boolean(item),
    );
    const actionItems =
        supplementalItems.length > 0
            ? deleteIndex >= 0
                ? [...items.slice(0, deleteIndex), ...supplementalItems, ...items.slice(deleteIndex)]
                : [...items, ...supplementalItems]
            : items;
    const filteredItems = filterWorkspaceResourceActionItems(actionItems, search);

    const stopTriggerPropagation = (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
        if (stopPropagation) {
            event.stopPropagation();
        }
    };

    const renderItem = (item: WorkspaceResourceActionMenuItem, itemIndex: number) => {
        const disabled = getWorkspaceResourceActionItemDisabled(item, access);

        return (
            <Fragment key={item.key}>
                {item.children?.length ? (
                    <Menu.SubMenu
                        key={item.key}
                        disabled={disabled}
                        expandIcon={<div />}
                        popupOffset={[16, 0]}
                        popupStyle={{
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            width: 16 * 18,
                        }}
                        title={
                            <span
                                style={{
                                    alignItems: 'center',
                                    display: 'grid',
                                    gap: '0.5rem',
                                    gridTemplateColumns: 'auto 1fr auto',
                                }}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                                <TbChevronRight style={{ color: token.colorTextTertiary }} />
                            </span>
                        }
                    >
                        <div style={{ marginBottom: '0.25rem', padding: '0 0.75rem' }}>
                            <small style={{ color: token.colorTextTertiary }}>{item.label}</small>
                        </div>
                        {item.children.map((child) => (
                            <Menu.Item
                                key={child.key}
                                danger={child.danger}
                                disabled={getWorkspaceResourceActionItemDisabled(child, access)}
                                icon={child.icon}
                                onClick={({ domEvent }) => {
                                    if (stopPropagation) {
                                        domEvent.stopPropagation();
                                    }

                                    child.onClick?.();
                                }}
                            >
                                {child.label}
                            </Menu.Item>
                        ))}
                    </Menu.SubMenu>
                ) : (
                    <Menu.Item
                        key={item.key}
                        danger={item.danger}
                        disabled={disabled}
                        icon={item.icon}
                        onClick={({ domEvent }) => {
                            if (stopPropagation) {
                                domEvent.stopPropagation();
                            }

                            item.onClick?.();
                        }}
                    >
                        {item.label}
                    </Menu.Item>
                )}
                {shouldRenderWorkspaceResourceActionDivider(filteredItems, itemIndex, search) ? (
                    <Menu.Divider key={`divider-${itemIndex}`} />
                ) : undefined}
            </Fragment>
        );
    };

    const actionsDropdown = (
        <Dropdown
            key={triggerKey}
            onOpenChange={onOpenChange}
            open={open}
            trigger={['click']}
            placement={placement}
            popupRender={() => (
                <DropdownPanel
                    footer={footer}
                    maxHeight={menuStyle?.maxHeight ?? defaultMenuStyle.maxHeight}
                    minWidth={menuStyle?.minWidth ?? defaultMenuStyle.minWidth}
                    search={{
                        placeholder: `Search ${resourceLabel} Actions...`,
                        setValue: setSearch,
                        value: search,
                    }}
                    style={menuStyle}
                >
                    <DropdownPanelMenu
                        onClick={({ domEvent }) => {
                            if (stopPropagation) {
                                domEvent.stopPropagation();
                            }

                            setSearch('');
                            onOpenChange?.(false);
                        }}
                    >
                        {search.trim() ? undefined : beforeItems}
                        {filteredItems.length !== 0 && !loading ? (
                            filteredItems.map(renderItem)
                        ) : loading ? (
                            <Menu.Item disabled key='loadingState'>
                                <BaseSkeleton paragraph={{ rows: 8 }} />
                            </Menu.Item>
                        ) : (
                            <Menu.Item disabled key='emptyState'>
                                <EmptySection
                                    height={16 * 8}
                                    icon={<TbSearch size='75%' style={{ color: token.colorTextQuaternary }} />}
                                    label='No Matching Actions'
                                    description={
                                        <>
                                            No actions match <strong>{search}</strong>.
                                        </>
                                    }
                                />
                            </Menu.Item>
                        )}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
        >
            <span
                onClick={stopTriggerPropagation}
                onKeyDown={stopTriggerPropagation}
                onMouseDown={stopTriggerPropagation}
            >
                {children}
            </span>
        </Dropdown>
    );

    return (
        <>
            {tagSubject ? (
                <TagAssignmentDropdown
                    onOpenChange={setTagsOpen}
                    open={tagsOpen}
                    placement={placement}
                    resourceLabel={history?.title ?? resourceLabel}
                    subject={tagSubject}
                    trigger={[]}
                >
                    {actionsDropdown}
                </TagAssignmentDropdown>
            ) : (
                actionsDropdown
            )}
            {history && isMergeableResourceType(history.resourceType) ? (
                <MergeResourceModal open={mergeOpen} setOpen={setMergeOpen} source={history} />
            ) : undefined}
        </>
    );
};

export default WorkspaceResourceActionsDropdown;
